import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { activeSiteConfig } from "@config/site/site-config";
import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import type { TextProps } from "@ui/components/content/text/Text";
import { Text } from "@ui/components/content/text/Text";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { defaultUnderlineStyle } from "@ui/css/common";
import { isEmpty } from "lodash-es";
import { forwardRef } from "react";
import { hasWindow } from "@lib/utils/hasWindow";
import { iframeStyle } from "@lib/live-chat/microsoftLiveChatScript/constants";
import { getIsChatAgentAvailable } from "@lib/live-chat/microsoftLiveChatScript/openChatFromLink";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

interface IModalLinkProps extends TextProps {
    link: ILink | IPagePath;
}
export interface MicrosoftWindow extends Window {
    handleLivechatReadyEvent?: () => void;
    handleLivechatMinimizeEvent?: () => void;
    Microsoft?: {
        Omnichannel: {
            LiveChatWidget: {
                SDK: {
                    startChat: () => void;
                    setContextProvider: (provider: () => any) => void;
                    startProactiveChat: (config: any, flag: boolean) => void;
                    closeChat: () => void;
                    getAgentAvailability: () => void;
                };
            };
        };
    };
}

export const ModalLink = forwardRef<HTMLParagraphElement, IModalLinkProps>(
    ({ link, children, ...rest }, ref) => {
        const _link = link as ILink;
        const label = _link?.fields?.label || "";
        const description = _link?.fields?.description || "";
        const descriptionRichText = _link?.fields?.descriptionRichText || "";
        const isChatLink = _link?.fields?.modal === "chat";
        const chatSolution = process.env.NEXT_PUBLIC_LIVE_CHAT_SOLUTION || "";
        const isLiveChatEnabled = activeSiteConfig?.liveChat;

        const { showModal } = useGlobalModalContext();

        const handleAvailableChat = (microsoftWindow: MicrosoftWindow) => {
            microsoftWindow.Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            const iframe = document.querySelector(
                "#Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window"
            );

            if (iframe) {
                iframe.setAttribute("style", iframeStyle);
            }
        };

        const onClick = (event: React.MouseEvent<HTMLElement>) => {
            const microsoftWindow = window as MicrosoftWindow;
            if (hasWindow && microsoftWindow.Microsoft && isChatLink && chatSolution === "D365") {
                getIsChatAgentAvailable(microsoftWindow)
                    .then((isAvailable: boolean) => {
                        isAvailable
                            ? handleAvailableChat(microsoftWindow)
                            : showModal(ModalTypes.chatIsNotAvailable);
                    })
                    .catch((error) => {
                        Logger.error(ServiceType.MICROSOFT, "Error loading Microsoft Chat", {
                            tag: LogTag.LIVE_CHAT,
                            error: error,
                        });
                    });
            } else {
                showModal(_link?.fields?.modal, { description, label, descriptionRichText });
            }
            event.preventDefault();
        };

        return (
            <Text
                data-type="modal-link"
                as="button"
                color="inherit"
                fontSize="inherit"
                cursor="pointer"
                onClick={onClick}
                data-testid={`${_link?.fields?.modal}-link`}
                {...rest}
                ref={ref}
                sx={{
                    // We need to spread the sx prop so it's not overwritten by the default styles
                    ...rest?.sx,
                    // We are not using Link here, so we need to overwrite the default styles of the Button with our link underline type styles
                    transitionProperty: "common",
                    transitionDuration: "fast",
                    transitionTimingFunction: "ease-out",
                    ...defaultUnderlineStyle,
                    fontWeight: "bold",
                    textDecoration: "underline",
                    _hover: {
                        color: "gray.500",
                        textDecorationColor: "gray.500",
                    },
                    // We need this because the way of how Contentful hanldes rich text.
                    // If editors use a mark, then it will be wrapped in a span tag, so in this way we force no matter what editors use, we will have consistency
                    "& span": {
                        fontWeight: "bold",
                        textDecoration: "underline",
                    },
                    _focusVisible: {
                        outline: "2px solid",
                        outlineColor: "gray.500",
                        outlineOffset: "2px",
                        textDecoration: "none",
                    },
                }}
            >
                {isEmpty(children) ? label : children}
                {isChatLink && isLiveChatEnabled && (
                    <ECCOIcon ml={1} mt={1} size="md" fill={"currentcolor"} name="wechat" />
                )}
            </Text>
        );
    }
);

ModalLink.displayName = "ModalLink";
